if (!customElements.get('tf-hero-banners')) {
  customElements.define('tf-hero-banners', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.init());
    }

    init() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.viewportObserver?.disconnect();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.slides = [...this.querySelectorAll('.tf-banner-slide')];
      this.dots = [...this.querySelectorAll('[data-slide-index]')];
      this.motion = this.querySelector('.tf-banner-motion');
      this.index = 0;
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.reduced.matches;
      this.blocked = false;
      const bounds = this.getBoundingClientRect();
      this.visible = bounds.bottom > -200 && bounds.top < window.innerHeight + 200;
      this.focused = false;
      this.hovered = false;
      this.editorSelected = false;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      if (!this.slides.length) return;

      this.addEventListener('click', event => {
        const direction = event.target.closest('[data-direction]');
        const dot = event.target.closest('[data-slide-index]');
        if (direction) this.select(this.index + Number(direction.dataset.direction));
        if (dot) this.select(Number(dot.dataset.slideIndex));
        if (event.target.closest('.tf-banner-motion')) {
          this.paused = this.blocked ? false : !this.paused;
          this.blocked = false;
          this.syncMedia();
          this.schedule();
        }
      }, options);
      this.addEventListener('keydown', event => {
        if (event.target.matches('input,textarea,select') || event.target.isContentEditable) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, options);
      this.addEventListener('touchstart', event => {
        this.touch = event.touches.length === 1 && !event.target.closest('a,button')
          ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { ...options, passive: true });
      this.addEventListener('touchend', event => {
        if (!this.touch || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.select(this.index + (dx < 0 ? 1 : -1));
        this.touch = null;
      }, { ...options, passive: true });
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      }, options);
      this.mobile.addEventListener('change', () => this.syncMedia(), options);
      this.reduced.addEventListener('change', () => {
        this.paused = this.reduced.matches;
        this.syncMedia();
        this.schedule();
      }, options);
      window.addEventListener('pageshow', () => { this.syncMedia(); this.schedule(); }, options);
      window.addEventListener('online', () => this.syncMedia(), options);
      const retryFromGesture = event => {
        if (event.target.closest('.tf-banner-motion') || this.paused || this.reduced.matches) return;
        this.syncMedia();
      };
      document.addEventListener('touchend', retryFromGesture, { ...options, passive: true });
      document.addEventListener('click', retryFromGesture, options);
      document.addEventListener('visibilitychange', () => { this.syncMedia(); this.schedule(); }, options);
      document.addEventListener('shopify:block:select', event => {
        const selected = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (selected < 0) return;
        this.editorSelected = true;
        this.select(selected);
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        if (!this.slides.some(slide => slide.dataset.blockId === event.detail?.blockId)) return;
        this.editorSelected = false;
        this.schedule();
      }, options);
      this.querySelectorAll('video').forEach(video => {
        video.addEventListener('playing', () => {
          video.classList.add('is-playing');
          this.blocked = false;
          this.updateMotion(true);
        }, options);
        video.addEventListener('canplay', () => { if (video.paused && !this.blocked) this.syncMedia(); }, options);
        video.addEventListener('error', () => video.classList.remove('is-playing'), options);
        if (!video.paused && video.readyState >= 2) video.classList.add('is-playing');
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.syncMedia();
        this.schedule();
      }, { rootMargin: '200px 0px' });
      this.observer.observe(this);
      // Safari can defer off-screen playback; retry on actual viewport entry.
      this.viewportObserver = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) this.syncMedia();
      });
      this.viewportObserver.observe(this);
      this.select(0);
    }

    select(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.syncMedia();
      this.schedule();
    }

    syncMedia() {
      let activeHasVideo = false;
      this.slides.forEach((slide, i) => {
        const media = slide.querySelector('.tf-banner-media');
        const video = media?.querySelector('video');
        if (!video) return;
        const source = this.mobile.matches ? media.dataset.mobileVideo : media.dataset.desktopVideo;
        const active = i === this.index && !!source;
        if (active) activeHasVideo = true;
        if (!active) {
          video.pause();
          video.classList.remove('is-playing');
          if (video.hasAttribute('src')) {
            video.removeAttribute('src');
            video.load();
          }
          return;
        }
        const shouldPlay = !this.paused && !document.hidden && (this.visible || video.hasAttribute('src'));
        if (video.getAttribute('src') !== source) {
          video.pause();
          video.classList.remove('is-playing');
          if (video.hasAttribute('src')) { video.removeAttribute('src'); video.load(); }
          if (!shouldPlay) return;
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          video.autoplay = true;
          video.preload = 'auto';
          video.src = source;
          video.load();
        }
        if (shouldPlay && video.paused) {
          video.play().catch(error => {
            // Keep the poster if the browser blocks autoplay; retry on return or gesture.
            if (error.name === 'NotAllowedError' && i === this.index && video.paused && this.visible && !document.hidden) {
              this.blocked = true;
              this.updateMotion(activeHasVideo);
              this.schedule();
            }
          });
        } else if (!shouldPlay) video.pause();
      });
      this.updateMotion(activeHasVideo);
    }

    updateMotion(hasVideo) {
      if (!this.motion) return;
      this.motion.hidden = !hasVideo && !this.autoplay;
      this.motion.classList.toggle('is-paused', this.paused || this.blocked);
      this.motion.classList.toggle('tf-autoplay-blocked', !!this.blocked);
      this.motion.setAttribute('aria-label', (this.paused || this.blocked) ? this.dataset.playLabel : this.dataset.pauseLabel);
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.paused || !this.visible || document.hidden || this.focused || this.hovered || this.editorSelected) return;
      this.timer = setTimeout(() => this.select(this.index + 1), Number(this.dataset.interval) || 7000);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timer);
      this.abort?.abort();
      this.observer?.disconnect();
      this.viewportObserver?.disconnect();
      this.querySelectorAll('video').forEach(video => { video.pause(); video.removeAttribute('src'); video.load(); });
    }
  });
}
